import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getCustomerToken } from "../utils/CustomerAuth";
import { reviewService } from "../services/reviewServices";

const API_ROOT = "https://api.ifjona.com";
const API_BASE = `${API_ROOT}/api`;

/** ✅ pick any possible key from backend */
const pickImageValue = (im) => {
  if (!im) return null;
  // backend might return: {url}, {path}, {image}, {image_url}, {full_url}
  return im.url || im.path || im.image_url || im.full_url || im.image || null;
};

// if backend returns only "path" like "reviews/xxx.jpg"
const toPublicUrl = (pathOrUrl) => {
  if (!pathOrUrl) return null;
  const s = String(pathOrUrl).trim();
  if (!s) return null;

  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  if (s.startsWith("/storage/")) return `${API_ROOT}${s}`;
  if (s.startsWith("storage/")) return `${API_ROOT}/${s}`;
  // typical: "reviews/abc.jpg"
  return `${API_ROOT}/storage/${s.replace(/^\/+/, "")}`;
};

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "2-digit",
    });
  } catch {
    return String(iso);
  }
};

const CSS = `
  :root {
    --rv-primary:  #299e60;
    --rv-light:    #f3faf2;
    --rv-border:   #c4e8d1;
    --rv-hover:    #228a52;
    --rv-text:     #1a5c3a;
    --rv-star:     #299e60;
    --rv-star-off: #e2e8f0;
  }

  .rv-wrap { padding: 48px 0 64px; font-family: inherit; }

  .rv-section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 28px;
    padding-bottom: 16px;
    border-bottom: 2px solid var(--rv-border);
  }

  .rv-section-title {
    font-size: 20px;
    font-weight: 800;
    color: #1d2939;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .rv-section-title span {
    background: var(--rv-light);
    color: var(--rv-primary);
    border: 1px solid var(--rv-border);
    border-radius: 20px;
    font-size: 12px;
    font-weight: 700;
    padding: 2px 10px;
  }

  .rv-sort {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
  }

  .rv-sort select {
    border: 1px solid var(--rv-border);
    border-radius: 7px;
    background: var(--rv-light);
    color: #1d2939;
    font-size: 12px;
    font-weight: 600;
    padding: 5px 10px;
    outline: none;
    cursor: pointer;
  }

  .rv-summary {
    background: var(--rv-light);
    border: 1px solid var(--rv-border);
    border-radius: 14px;
    padding: 24px 28px;
    display: flex;
    align-items: center;
    gap: 32px;
    flex-wrap: wrap;
    margin-bottom: 24px;
  }

  .rv-big-score { text-align: center; flex-shrink: 0; }
  .rv-big-number {
    font-size: 52px;
    font-weight: 900;
    color: var(--rv-primary);
    line-height: 1;
    margin-bottom: 6px;
  }
  .rv-big-stars { display: flex; gap: 3px; justify-content: center; margin-bottom: 4px; }
  .rv-big-star { font-size: 18px; color: var(--rv-star); }
  .rv-big-star.off { color: var(--rv-star-off); }
  .rv-big-count { font-size: 12px; color: #64748b; font-weight: 600; }
  .rv-divider { width: 1px; height: 80px; background: var(--rv-border); flex-shrink: 0; }

  .rv-bars { flex: 1; min-width: 200px; display: flex; flex-direction: column; gap: 8px; }
  .rv-bar-row { display: flex; align-items: center; gap: 10px; }
  .rv-bar-label { font-size: 12px; font-weight: 700; color: #64748b; white-space: nowrap; width: 32px; text-align: right; }
  .rv-bar-track { flex: 1; height: 8px; background: #e2e8f0; border-radius: 4px; overflow: hidden; }
  .rv-bar-fill { height: 100%; background: var(--rv-primary); border-radius: 4px; transition: width 0.6s ease; }
  .rv-bar-count { font-size: 11px; font-weight: 700; color: #94a3b8; width: 30px; text-align:right; }

  .rv-chips { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 20px; }

  .rv-chip {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 20px;
    padding: 5px 14px;
    font-size: 12px;
    font-weight: 700;
    color: #64748b;
    cursor: pointer;
    transition: all 0.15s;
  }
  .rv-chip:hover { border-color: var(--rv-border); color: var(--rv-primary); background: var(--rv-light); }
  .rv-chip.active { background: var(--rv-primary); border-color: var(--rv-primary); color: #fff; }

  .rv-card {
    border: 1px solid #ececec;
    border-radius: 12px;
    padding: 20px 22px;
    margin-bottom: 14px;
    background: #fff;
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  .rv-card:hover { border-color: var(--rv-border); box-shadow: 0 2px 12px rgba(41,158,96,0.07); }

  .rv-card-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 10px;
    flex-wrap: wrap;
  }
  .rv-user { display: flex; align-items: center; gap: 12px; }
  .rv-avatar {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: var(--rv-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    font-weight: 800;
    color: #fff;
    flex-shrink: 0;
  }
  .rv-user-name { font-size: 14px; font-weight: 700; color: #1d2939; margin-bottom: 2px; }
  .rv-user-date { font-size: 11px; color: #94a3b8; font-weight: 600; }

  .rv-card-star { font-size: 14px; color: var(--rv-star); }
  .rv-card-star.off { color: var(--rv-star-off); }

  .rv-verified {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: var(--rv-light);
    border: 1px solid var(--rv-border);
    border-radius: 20px;
    padding: 2px 10px;
    font-size: 10px;
    font-weight: 700;
    color: var(--rv-text);
    white-space: nowrap;
  }

  .rv-card-body { font-size: 13.5px; color: #475467; line-height: 1.7; margin-bottom: 12px; }

  .rv-card-footer {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
    padding-top: 10px;
    border-top: 1px solid #f5f5f5;
    margin-top: 12px;
  }

  .rv-helpful {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px 10px;
    border-radius: 6px;
    transition: background 0.13s, color 0.13s;
  }
  .rv-helpful:hover { background: var(--rv-light); color: var(--rv-primary); }
  .rv-helpful.active { color: var(--rv-primary); }

  .rv-images {
    display:flex;
    gap:10px;
    flex-wrap:wrap;
    margin-top: 10px;
  }
  .rv-img {
    width: 64px;
    height: 64px;
    border-radius: 10px;
    border: 1px solid #e5e7eb;
    object-fit: cover;
  }

  .rv-replies {
    margin-top: 14px;
    border-left: 3px solid var(--rv-border);
    padding-left: 14px;
    display:flex;
    flex-direction: column;
    gap: 10px;
  }
  .rv-reply-card {
    background: #fbfffc;
    border: 1px solid #e7f5ec;
    border-radius: 12px;
    padding: 12px 14px;
  }
  .rv-reply-head {
    display:flex;
    justify-content: space-between;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 6px;
  }
  .rv-reply-name { font-size: 13px; font-weight: 800; color:#1d2939; }
  .rv-reply-date { font-size: 11px; font-weight: 600; color:#94a3b8; }
  .rv-reply-text { font-size: 13px; color:#475467; line-height:1.6; margin:0; }

  .rv-write {
    background: var(--rv-light);
    border: 1px solid var(--rv-border);
    border-radius: 14px;
    padding: 26px 28px;
    margin-top: 28px;
  }
  .rv-write-title {
    font-size: 16px;
    font-weight: 800;
    color: #1d2939;
    margin: 0 0 18px;
    padding-bottom: 14px;
    border-bottom: 1px solid var(--rv-border);
  }

  .rv-star-picker { display: flex; gap: 6px; margin-bottom: 18px; }

  .rv-star-pick {
    font-size: 26px;
    cursor: pointer;
    color: var(--rv-star-off);
    transition: color 0.12s, transform 0.1s;
    background: none;
    border: none;
    padding: 0;
    line-height: 1;
  }
  .rv-star-pick.on { color: var(--rv-star); }
  .rv-star-pick:hover { transform: scale(1.15); }

  .rv-field { margin-bottom: 14px; }
  .rv-label {
    display: block;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.7px;
    color: #344054;
    margin-bottom: 6px;
  }

  .rv-input, .rv-textarea {
    width: 100%;
    background: #fff;
    border: 1.5px solid var(--rv-border);
    border-radius: 9px;
    padding: 10px 14px;
    font-size: 13.5px;
    color: #1d2939;
    outline: none;
    transition: border-color 0.15s, box-shadow 0.15s;
    box-sizing: border-box;
    font-family: inherit;
  }
  .rv-input:focus, .rv-textarea:focus {
    border-color: var(--rv-primary);
    box-shadow: 0 0 0 3px rgba(41,158,96,0.1);
  }
  .rv-textarea { resize: vertical; min-height: 100px; }

  .rv-upload {
    display:flex;
    align-items:center;
    justify-content: space-between;
    gap: 10px;
    flex-wrap: wrap;
    margin-top: 8px;
  }
  .rv-upload small { color:#64748b; font-weight:600; }

  .rv-submit {
    background: var(--rv-primary);
    color: #fff;
    border: none;
    border-radius: 9px;
    padding: 12px 28px;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    transition: background 0.15s, transform 0.1s, box-shadow 0.15s;
    box-shadow: 0 4px 12px rgba(41,158,96,0.25);
    font-family: inherit;
  }
  .rv-submit:hover { background: var(--rv-hover); transform: translateY(-1px); box-shadow: 0 6px 16px rgba(41,158,96,0.32); }
  .rv-submit:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }

  .rv-pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    margin-top: 24px;
    flex-wrap: wrap;
  }
  .rv-page-btn {
    min-width: 34px;
    height: 34px;
    padding: 0 10px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    background: #fff;
    font-size: 13px;
    font-weight: 700;
    color: #64748b;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.13s;
    font-family: inherit;
  }
  .rv-page-btn:hover { border-color: var(--rv-border); color: var(--rv-primary); background: var(--rv-light); }
  .rv-page-btn.active { background: var(--rv-primary); border-color: var(--rv-primary); color: #fff; }

  .rv-state {
    border: 1px dashed #d1fae5;
    background: #f0fdf4;
    border-radius: 12px;
    padding: 14px 16px;
    color: #14532d;
    font-weight: 700;
    font-size: 13px;
    margin-bottom: 14px;
  }
`;

const StarRow = ({ rating, size = 14 }) => (
  <div style={{ display: "flex", gap: 2 }}>
    {[1, 2, 3, 4, 5].map((i) => (
      <span
        key={i}
        className={`rv-card-star${i > (Number(rating) || 0) ? " off" : ""}`}
        style={{ fontSize: size }}
      >
        ★
      </span>
    ))}
  </div>
);

export default function ProductReviews({ productId: productIdProp }) {
  const navigate = useNavigate();
  const params = useParams();
  const productId = productIdProp ?? params?.id;

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [filter, setFilter] = useState("All");
  const [sort, setSort] = useState("Most Recent");

  // pagination
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [pager, setPager] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
  });

  const [reviews, setReviews] = useState([]);

  // write review form
  const [hoveredStar, setHoveredStar] = useState(0);
  const [selectedStar, setSelectedStar] = useState(0);
  const [form, setForm] = useState({ review: "" });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // ✅ helpful local states (toggle)
  const [helpfulState, setHelpfulState] = useState(() => new Map()); // reviewId -> boolean
  const [helpfulBusy, setHelpfulBusy] = useState(() => new Set()); // reviewId -> loading

  // ✅ current user (for edit/delete)
  const [me, setMe] = useState(null);

  // ✅ inline edit state (NO MODAL)
  const [editingId, setEditingId] = useState(null);
  const [editHoveredStar, setEditHoveredStar] = useState(0);
  const [editStar, setEditStar] = useState(0);
  const [editText, setEditText] = useState("");
  const [editFiles, setEditFiles] = useState([]);
  const [editSubmitting, setEditSubmitting] = useState(false);

  const FILTERS = [
    "All",
    "5 Stars",
    "4 Stars",
    "3 Stars",
    "2 Stars",
    "1 Star",
    "With Photos",
  ];

  const ratingFilterValue = useMemo(() => {
    if (filter.endsWith("Stars")) return Number(filter.split(" ")[0]);
    if (filter === "1 Star") return 1;
    return null;
  }, [filter]);

  // ✅ load current customer info (optional but needed to show edit/delete)
  useEffect(() => {
    const token = getCustomerToken();
    if (!token) return;

    (async () => {
      try {
        const res = await fetch(`${API_BASE}/customer/me`, {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) return;
        const j = await res.json();
        setMe(j?.data || j);
      } catch {}
    })();
  }, []);

  const fetchReviews = async (targetPage = page) => {
    if (!productId) return;
    setLoading(true);
    setErr("");

    try {
      const qs = new URLSearchParams();
      qs.set("per_page", String(perPage));
      qs.set("page", String(targetPage));
      if (ratingFilterValue) qs.set("rating", String(ratingFilterValue));

      const res = await fetch(
        `${API_BASE}/products/${productId}/reviews?${qs.toString()}`,
        { headers: { Accept: "application/json" } }
      );

      if (!res.ok) throw new Error(`Failed to load reviews (${res.status})`);
      const json = await res.json();

      const data = Array.isArray(json?.data) ? json.data : [];
      setReviews(data);

      // ✅ Seed is_helpful state from API so thumbs stays active after reload
      setHelpfulState((prev) => {
        const n = new Map(prev);
        for (const r of data) {
          if (r?.is_helpful !== undefined) n.set(r.id, !!r.is_helpful);
        }
        return n;
      });

      setPager({
        current_page: json?.current_page ?? targetPage,
        last_page: json?.last_page ?? 1,
        total: json?.total ?? data.length,
      });
    } catch (e) {
      setReviews([]);
      setPager({ current_page: 1, last_page: 1, total: 0 });
      setErr(e?.message || "Failed to load reviews");
    } finally {
      setLoading(false);
    }
  };

  // ✅ when productId/filter changes => reset page and refetch 1
  useEffect(() => {
    setPage(1);
    if (productId) fetchReviews(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, filter]);

  useEffect(() => {
    fetchReviews(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, page, perPage]);

  const visibleReviews = useMemo(() => {
    let arr = Array.isArray(reviews) ? [...reviews] : [];

    if (filter === "With Photos") {
      arr = arr.filter((r) => (r?.images || []).length > 0);
    }

    if (sort === "Highest Rated") {
      arr.sort((a, b) => (Number(b?.rating) || 0) - (Number(a?.rating) || 0));
    } else if (sort === "Lowest Rated") {
      arr.sort((a, b) => (Number(a?.rating) || 0) - (Number(b?.rating) || 0));
    }
    return arr;
  }, [reviews, filter, sort]);

  const stats = useMemo(() => {
    const all = (Array.isArray(reviews) ? reviews : []).filter((r) => r?.rating != null);
    const total = all.length;
    const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    let sum = 0;

    for (const r of all) {
      const rt = Math.max(1, Math.min(5, Number(r.rating) || 0));
      counts[rt] += 1;
      sum += rt;
    }

    const avg = total ? sum / total : 0;
    const bars = [5, 4, 3, 2, 1].map((k) => {
      const c = counts[k] || 0;
      const pct = total ? Math.round((c / total) * 100) : 0;
      return { label: `${k} ★`, count: c, pct };
    });

    return { avg, total, bars };
  }, [reviews]);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!productId) return;

    const token = getCustomerToken();
    if (!token) {
      navigate("/login", { state: { from: `/product-details/${productId}` } });
      return;
    }
    if (!selectedStar) return;

    try {
      setSubmitting(true);

      const fd = new FormData();
      fd.append("rating", String(selectedStar));
      fd.append("review", form.review);
      for (const f of files) fd.append("images[]", f);

      const res = await fetch(`${API_BASE}/products/${productId}/reviews`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: fd,
      });

      if (!res.ok) {
        let msg = `Submit failed (${res.status})`;
        try {
          const j = await res.json();
          msg = j?.message || msg;
        } catch {}
        throw new Error(msg);
      }

      setSubmitted(true);
      setForm({ review: "" });
      setFiles([]);
      setSelectedStar(0);
      setTimeout(() => setSubmitted(false), 4000);

      setPage(1);
      fetchReviews(1);
    } catch (e2) {
      alert(e2?.message || "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  // ✅ Helpful toggle (like/unlike)
  const toggleHelpful = async (reviewId) => {
    if (!reviewId) return;

    const token = getCustomerToken();
    if (!token) {
      navigate("/login", { state: { from: `/product-details/${productId}` } });
      return;
    }

    if (helpfulBusy.has(reviewId)) return;
    setHelpfulBusy((prev) => new Set(prev).add(reviewId));

    try {
      const j = await reviewService.toggleHelpful(reviewId);

      setHelpfulState((prev) => {
        const n = new Map(prev);
        n.set(reviewId, !!j?.is_helpful);
        return n;
      });

      setReviews((prev) =>
        (prev || []).map((r) =>
          String(r?.id) === String(reviewId)
            ? { ...r, helpful_count: Number(j?.helpful_count ?? r?.helpful_count ?? 0) }
            : r
        )
      );
    } catch (e) {
      alert(e?.response?.data?.message || e?.message || "Failed to toggle helpful");
    } finally {
      setHelpfulBusy((prev) => {
        const n = new Set(prev);
        n.delete(reviewId);
        return n;
      });
    }
  };

  // ✅ Start inline edit for a review (NO MODAL)
  const startEdit = (r) => {
    setEditingId(r?.id);
    setEditStar(Number(r?.rating || 0));
    setEditText(r?.review || "");
    setEditFiles([]);
    setEditHoveredStar(0);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditStar(0);
    setEditText("");
    setEditFiles([]);
    setEditHoveredStar(0);
  };

  // ✅ Submit edit (text + rating + add images)
  const submitEdit = async (reviewId) => {
    if (!productId || !reviewId) return;

    const token = getCustomerToken();
    if (!token) {
      navigate("/login", { state: { from: `/product-details/${productId}` } });
      return;
    }

    if (!editStar) return;

    try {
      setEditSubmitting(true);

      const j = await reviewService.updateProductReview({
        productId,
        reviewId,
        rating: editStar,
        review: editText,
        files: editFiles, // new images only
        removeImageIds: [], // no remove UI (design unchanged)
      });

      // update list locally
      // update list locally (✅ keep replies)
      const updated = j?.data || j;

      setReviews((prev) =>
        (prev || []).map((old) => {
          if (String(old?.id) !== String(reviewId)) return old;

          return {
            ...old,                 // ✅ keep replies + other fields
            ...updated,             // ✅ apply updated review/rating/images
            replies: old?.replies || [], // ✅ ensure replies never disappear
          };
        })
      );

      cancelEdit();
    } catch (e) {
      alert(e?.response?.data?.message || e?.message || "Failed to update review");
    } finally {
      setEditSubmitting(false);
    }
  };

  // ✅ Delete review
  const deleteReview = async (reviewId) => {
    if (!productId || !reviewId) return;

    const token = getCustomerToken();
    if (!token) {
      navigate("/login", { state: { from: `/product-details/${productId}` } });
      return;
    }

    const ok = window.confirm("Are you sure you want to delete your review?");
    if (!ok) return;

    try {
      await reviewService.deleteProductReview({ productId, reviewId });

      setReviews((prev) => (prev || []).filter((r) => String(r.id) !== String(reviewId)));

      // if deleting the one currently editing
      if (String(editingId) === String(reviewId)) cancelEdit();
    } catch (e) {
      alert(e?.response?.data?.message || e?.message || "Failed to delete review");
    }
  };

  const pagesToShow = useMemo(() => {
    const last = Number(pager.last_page || 1);
    const cur = Number(pager.current_page || 1);
    const start = Math.max(1, cur - 2);
    const end = Math.min(last, start + 4);
    const out = [];
    for (let p = start; p <= end; p++) out.push(p);
    return out;
  }, [pager]);

  // ✅ preview urls cleanup (memory leak fix, design unchanged)
  const previewUrls = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  useEffect(() => {
    return () => {
      previewUrls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [previewUrls]);

  // ✅ edit preview urls cleanup
  const editPreviewUrls = useMemo(
    () => editFiles.map((f) => URL.createObjectURL(f)),
    [editFiles]
  );
  useEffect(() => {
    return () => {
      editPreviewUrls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [editPreviewUrls]);

  return (
    <section className="rv-wrap">
      <style>{CSS}</style>
      <div className="container container-lg">
        {/* Header */}
        <div className="rv-section-head">
          <h2 className="rv-section-title">
            Customer Reviews
            <span>{pager.total} reviews</span>
          </h2>

          <div className="rv-sort">
            Sort by:
            <select value={sort} onChange={(e) => setSort(e.target.value)}>
              <option>Most Recent</option>
              <option>Highest Rated</option>
              <option>Lowest Rated</option>
            </select>
          </div>
        </div>

        <div className="row gy-4">
          <div className="col-lg-8">
            {/* States */}
            {loading && <div className="rv-state">Loading reviews…</div>}
            {err && !loading && (
              <div
                className="rv-state"
                style={{
                  background: "#fff7ed",
                  borderColor: "#fed7aa",
                  color: "#9a3412",
                }}
              >
                {err}
              </div>
            )}

            {/* Summary */}
            <div className="rv-summary">
              <div className="rv-big-score">
                <div className="rv-big-number">{stats.avg ? stats.avg.toFixed(1) : "0.0"}</div>
                <div className="rv-big-stars">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <span
                      key={i}
                      className={`rv-big-star${i > Math.round(stats.avg || 0) ? " off" : ""}`}
                    >
                      ★
                    </span>
                  ))}
                </div>
                <div className="rv-big-count">{pager.total} total reviews</div>
              </div>

              <div className="rv-divider" />

              <div className="rv-bars">
                {stats.bars.map((b) => (
                  <div className="rv-bar-row" key={b.label}>
                    <span className="rv-bar-label">{b.label}</span>
                    <div className="rv-bar-track">
                      <div className="rv-bar-fill" style={{ width: `${b.pct}%` }} />
                    </div>
                    <span className="rv-bar-count">{b.count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Filters */}
            <div className="rv-chips">
              {FILTERS.map((f) => (
                <button
                  key={f}
                  type="button"
                  className={`rv-chip${filter === f ? " active" : ""}`}
                  onClick={() => setFilter(f)}
                >
                  {f}
                </button>
              ))}
            </div>

            {/* Review cards */}
            {!loading && visibleReviews.length === 0 ? (
              <div className="rv-state">No reviews found.</div>
            ) : (
              visibleReviews.map((r) => {
                const name = r?.customer?.name || r?.admin?.name || "User";
                const initial = String(name || "U").charAt(0).toUpperCase();
                const rating = r?.rating ?? 0;

                const imgs = Array.isArray(r?.images) ? r.images : [];
                const replies = Array.isArray(r?.replies) ? r.replies : [];

                const helpfulCount = Number(r?.helpful_count ?? 0);
                const isHelpful = helpfulState.get(r.id) === true;
                const isBusy = helpfulBusy.has(r.id);

                // ✅ is this review belongs to current customer?
                const isMine =
                  !!me?.id &&
                  (String(r?.customer?.id) === String(me.id) || String(r?.customer_id) === String(me.id));

                const isEditing = String(editingId) === String(r.id);

                return (
                  <div className="rv-card" key={r.id}>
                    <div className="rv-card-top">
                      <div className="rv-user">
                        <div className="rv-avatar">{initial}</div>
                        <div>
                          <div className="rv-user-name">{name}</div>
                          <div className="rv-user-date">{formatDate(r?.created_at)}</div>
                        </div>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-end",
                          gap: 6,
                        }}
                      >
                        <StarRow rating={rating} />
                        {r?.admin_id ? <span className="rv-verified">✓ Official</span> : null}
                      </div>
                    </div>

                    {/* ✅ INLINE EDITOR (NO MODAL) — design unchanged */}
                    {isEditing ? (
                      <>
                        <div className="rv-field" style={{ marginBottom: 12 }}>
                          <label className="rv-label">Update Rating</label>
                          <div className="rv-star-picker" style={{ marginBottom: 8 }}>
                            {[1, 2, 3, 4, 5].map((i) => (
                              <button
                                type="button"
                                key={i}
                                className={`rv-star-pick${(editHoveredStar || editStar) >= i ? " on" : ""}`}
                                onMouseEnter={() => setEditHoveredStar(i)}
                                onMouseLeave={() => setEditHoveredStar(0)}
                                onClick={() => setEditStar(i)}
                              >
                                ★
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="rv-field" style={{ marginBottom: 12 }}>
                          <label className="rv-label">Update Review</label>
                          <textarea
                            className="rv-textarea"
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            required
                          />
                        </div>

                        <div className="rv-field" style={{ marginBottom: 12 }}>
                          <label className="rv-label">Add Images (optional)</label>
                          <div className="rv-upload">
                            <input
                              type="file"
                              multiple
                              accept="image/png,image/jpeg,image/jpg,image/webp"
                              onChange={(e) => {
                                const list = Array.from(e.target.files || []);
                                setEditFiles(list.slice(0, 5));
                              }}
                            />
                            <small>Max 5 images (2MB each)</small>
                          </div>

                          {editFiles.length > 0 && (
                            <div className="rv-images" style={{ marginTop: 12 }}>
                              {editPreviewUrls.map((src, idx) => (
                                <img key={idx} className="rv-img" src={src} alt="preview" />
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="rv-card-footer">
                          <button
                            type="button"
                            className="rv-helpful"
                            onClick={() => submitEdit(r.id)}
                            disabled={editSubmitting || !editStar}
                            title="Save changes"
                          >
                            💾 Save {editSubmitting ? "..." : ""}
                          </button>

                          <button
                            type="button"
                            className="rv-helpful"
                            onClick={cancelEdit}
                            disabled={editSubmitting}
                            title="Cancel"
                          >
                            ✖ Cancel
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="rv-card-body">{r?.review || ""}</p>

                        {/* ✅ Parent Images */}
                        {imgs.length > 0 && (
                          <div className="rv-images">
                            {imgs.map((im) => {
                              const src = toPublicUrl(pickImageValue(im));
                              if (!src) return null;
                              return (
                                <img key={im?.id || src} className="rv-img" src={src} alt="Review" />
                              );
                            })}
                          </div>
                        )}

                        {/* Helpful footer + Edit/Delete */}
                        <div className="rv-card-footer">
                          <button
                            type="button"
                            className={`rv-helpful${isHelpful ? " active" : ""}`}
                            onClick={() => toggleHelpful(r.id)}
                            disabled={isBusy}
                            title={isHelpful ? "Remove helpful" : "Mark helpful"}
                          >
                            👍 Helpful ({helpfulCount})
                            {isBusy ? "..." : ""}
                          </button>

                          {/* ✅ Edit / Delete (only owner) */}
                          {isMine && (
                            <>
                              <button
                                type="button"
                                className="rv-helpful"
                                onClick={() => startEdit(r)}
                                title="Edit your review"
                              >
                                ✏️ Edit
                              </button>

                              <button
                                type="button"
                                className="rv-helpful"
                                onClick={() => deleteReview(r.id)}
                                title="Delete your review"
                              >
                                🗑 Delete
                              </button>
                            </>
                          )}
                        </div>

                        {/* ✅ Replies */}
                        {replies.length > 0 && (
                          <div className="rv-replies">
                            {replies.map((rep) => {
                              const repName = rep?.admin?.name || rep?.customer?.name || "Reply";
                              const repImgs = Array.isArray(rep?.images) ? rep.images : [];

                              return (
                                <div className="rv-reply-card" key={rep.id}>
                                  <div className="rv-reply-head">
                                    <div className="rv-reply-name">{repName}</div>
                                    <div className="rv-reply-date">{formatDate(rep?.created_at)}</div>
                                  </div>

                                  <p className="rv-reply-text">{rep?.review || ""}</p>

                                  {/* ✅ Reply Images */}
                                  {repImgs.length > 0 && (
                                    <div className="rv-images" style={{ marginTop: 10 }}>
                                      {repImgs.map((im) => {
                                        const src = toPublicUrl(pickImageValue(im));
                                        if (!src) return null;
                                        return (
                                          <img key={im?.id || src} className="rv-img" src={src} alt="Reply" />
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })
            )}

            {/* Pagination */}
            {pager.last_page > 1 && (
              <div className="rv-pagination">
                <button
                  type="button"
                  className="rv-page-btn"
                  disabled={pager.current_page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  ‹ Prev
                </button>

                {pagesToShow[0] > 1 && (
                  <>
                    <button type="button" className="rv-page-btn" onClick={() => setPage(1)}>
                      1
                    </button>
                    {pagesToShow[0] > 2 && (
                      <span style={{ padding: "0 6px", color: "#94a3b8", fontWeight: 800 }}>
                        …
                      </span>
                    )}
                  </>
                )}

                {pagesToShow.map((p) => (
                  <button
                    key={p}
                    type="button"
                    className={`rv-page-btn${p === pager.current_page ? " active" : ""}`}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>
                ))}

                {pagesToShow[pagesToShow.length - 1] < pager.last_page && (
                  <>
                    {pagesToShow[pagesToShow.length - 1] < pager.last_page - 1 && (
                      <span style={{ padding: "0 6px", color: "#94a3b8", fontWeight: 800 }}>
                        …
                      </span>
                    )}
                    <button type="button" className="rv-page-btn" onClick={() => setPage(pager.last_page)}>
                      {pager.last_page}
                    </button>
                  </>
                )}

                <button
                  type="button"
                  className="rv-page-btn"
                  disabled={pager.current_page >= pager.last_page}
                  onClick={() => setPage((p) => Math.min(pager.last_page, p + 1))}
                >
                  Next ›
                </button>
              </div>
            )}
          </div>

          {/* Write review */}
          <div className="col-lg-4">
            <div className="rv-write">
              <h3 className="rv-write-title">Write a Review</h3>

              {submitted && (
                <div
                  style={{
                    background: "#f0fdf4",
                    border: "1px solid #c4e8d1",
                    borderLeft: "3px solid #299e60",
                    borderRadius: 9,
                    padding: "10px 14px",
                    marginBottom: 18,
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#1a5c3a",
                  }}
                >
                  ✅ Review submitted! Thank you.
                </div>
              )}

              <form onSubmit={submitReview}>
                <div className="rv-field">
                  <label className="rv-label">Your Rating</label>
                  <div className="rv-star-picker">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <button
                        type="button"
                        key={i}
                        className={`rv-star-pick${(hoveredStar || selectedStar) >= i ? " on" : ""}`}
                        onMouseEnter={() => setHoveredStar(i)}
                        onMouseLeave={() => setHoveredStar(0)}
                        onClick={() => setSelectedStar(i)}
                      >
                        ★
                      </button>
                    ))}
                  </div>
                </div>

                <div className="rv-field">
                  <label className="rv-label" htmlFor="rv-review">
                    Your Review
                  </label>
                  <textarea
                    id="rv-review"
                    className="rv-textarea"
                    placeholder="Share your experience with this product…"
                    value={form.review}
                    onChange={(e) => setForm({ review: e.target.value })}
                    required
                  />
                </div>

                <div className="rv-field">
                  <label className="rv-label">Review Images (optional)</label>
                  <div className="rv-upload">
                    <input
                      type="file"
                      multiple
                      accept="image/png,image/jpeg,image/jpg,image/webp"
                      onChange={(e) => {
                        const list = Array.from(e.target.files || []);
                        setFiles(list.slice(0, 5));
                      }}
                    />
                    <small>Max 5 images (2MB each)</small>
                  </div>

                  {files.length > 0 && (
                    <div className="rv-images" style={{ marginTop: 12 }}>
                      {previewUrls.map((src, idx) => (
                        <img key={idx} className="rv-img" src={src} alt="preview" />
                      ))}
                    </div>
                  )}
                </div>

                <button type="submit" className="rv-submit" disabled={!selectedStar || submitting}>
                  {submitting ? "Submitting..." : "Submit Review"}
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M5 12h14M13 6l6 6-6 6" />
                  </svg>
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}